/**
 * Data Feed API Service (KAN-93)
 * CRUD for API keys and preview endpoint.
 * All endpoints require admin JWT auth.
 */

import { apiClient } from './client';
import type {
  ApiKeyDocument,
  ApiKeyCreateRequest,
  ApiKeyCreateResponse,
  ApiKeyUpdateRequest,
  DataFeedPreviewResult,
} from '../../types/apiKey';


class DataFeedService {

  // ========== Key CRUD ==========

  async listKeys(clientId: string): Promise<ApiKeyDocument[]> {
    const resp = await apiClient.get<ApiKeyDocument[]>(
      `/api/v1/data-feeds/${clientId}/keys`
    );
    return resp.data;
  }

  async getKey(clientId: string, keyId: string): Promise<ApiKeyDocument> {
    const resp = await apiClient.get<ApiKeyDocument>(
      `/api/v1/data-feeds/${clientId}/keys/${keyId}`
    );
    return resp.data;
  }

  async createKey(
    clientId: string,
    request: ApiKeyCreateRequest,
  ): Promise<ApiKeyCreateResponse> {
    const resp = await apiClient.post<ApiKeyCreateResponse>(
      `/api/v1/data-feeds/${clientId}/keys`,
      request
    );
    return resp.data;
  }

  async updateKey(
    clientId: string,
    keyId: string,
    request: ApiKeyUpdateRequest,
  ): Promise<ApiKeyDocument> {
    const resp = await apiClient.patch<ApiKeyDocument>(
      `/api/v1/data-feeds/${clientId}/keys/${keyId}`,
      request
    );
    return resp.data;
  }

  async revokeKey(clientId: string, keyId: string): Promise<void> {
    await apiClient.delete(
      `/api/v1/data-feeds/${clientId}/keys/${keyId}`
    );
  }

  // ========== Preview ==========

  async previewKey(
    clientId: string,
    keyId: string,
    params?: { dateFrom?: string; dateTo?: string; dateField?: string },
  ): Promise<DataFeedPreviewResult> {
    const resp = await apiClient.post<DataFeedPreviewResult>(
      `/api/v1/data-feeds/${clientId}/keys/${keyId}/preview`,
      params || {}
    );
    return resp.data;
  }

  // ========== Helpers ==========

  /** Find the API key linked to a specific template, if any. */
  async getKeyForTemplate(
    clientId: string,
    templateId: string,
  ): Promise<ApiKeyDocument | null> {
    const keys = await this.listKeys(clientId);
    return keys.find(k => k.templateId === templateId) || null;
  }
}

export const dataFeedService = new DataFeedService();
export default dataFeedService;
